import * as React from 'react';

import { useQuery } from '@apollo/client';
import { FIND_REPORT_ID_QUERY } from '@graphql/queries/AllReportIDsQuery';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import FormSection from 'components/ReportRequestV2/FormSection';
import SwitchField from 'components/ReportRequestV2/SwitchField';

import { StepProps } from './types';
import { setField } from '../model/reducer';
import { AdvancedOptions, OptimizationSettings, UpdateStrategy } from '../model/types';
import { isUuid } from '../model/validation';

interface Props extends StepProps {
  isSuperAdmin: boolean;
}

interface FoundReports {
  allReportJobs: { nodes: { reportId: string; clientName: string; projectName: string }[] };
}

/** Collapsed by default: options are rarely changed and would otherwise dominate the page. */
const OptionGroup: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <Accordion variant="outlined" disableGutters>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box>
        <Typography variant="subtitle1">{title}</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      <Box sx={{ display: 'grid', gap: 1 }}>{children}</Box>
    </AccordionDetails>
  </Accordion>
);

const InheritDataSourcesField: React.FC<StepProps> = ({ state, dispatch, errors }) => {
  const value = state.reportIdToInherit.trim();
  const valid = isUuid(value);
  const { data, loading } = useQuery<FoundReports>(FIND_REPORT_ID_QUERY, {
    skip: !valid,
    variables: { reportID: value },
  });
  const match = data?.allReportJobs.nodes[0];

  let helperText = 'Optional. Reuse the data sources of a previous successful report.';
  if (errors.reportIdToInherit) helperText = errors.reportIdToInherit;
  else if (value && !valid) helperText = 'Must be a report ID (UUID)';
  else if (valid && loading) helperText = 'Looking up the report…';
  else if (valid && match) helperText = `Found: ${match.clientName} — ${match.projectName}`;
  else if (valid && data) helperText = 'No successful report has this ID';

  return (
    <TextField
      label="Report ID to inherit data sources from"
      value={state.reportIdToInherit}
      onChange={(e) => dispatch(setField('reportIdToInherit', e.target.value))}
      error={!!errors.reportIdToInherit || (!!value && !valid) || (valid && !!data && !match)}
      helperText={helperText}
      fullWidth
      sx={{ maxWidth: 560, mt: 1 }}
    />
  );
};

const UPDATE_STRATEGIES: { value: UpdateStrategy; label: string; description: string }[] = [
  {
    value: 'ensure_exact_pq',
    label: 'Ensure exact PQ',
    description: 'Existing products keep exactly the PQ they had on the original report.',
  },
  {
    value: 'version_controled_translation',
    label: 'Version controlled translation',
    description: 'Re-uses the translation model version of the original report.',
  },
  {
    value: 'standard',
    label: 'Standard',
    description: 'Re-runs everything with the latest models. Existing PQs may shift.',
  },
];

const OptionsStep: React.FC<Props> = (props) => {
  const { state, dispatch, isSuperAdmin } = props;
  const adv = state.advanced;
  const opt = state.optimization;
  const patchAdv = (patch: Partial<AdvancedOptions>) => dispatch({ type: 'patchAdvanced', patch });
  const patchOpt = (patch: Partial<OptimizationSettings>) =>
    dispatch({ type: 'patchOptimization', patch });

  const advSwitch = (key: keyof AdvancedOptions, label: string, description?: string, disabledReason?: string) => (
    <SwitchField
      key={key}
      label={label}
      description={description}
      checked={adv[key]}
      onChange={(checked) => patchAdv({ [key]: checked })}
      disabled={!!disabledReason}
      disabledReason={disabledReason}
    />
  );

  const superAdmin = isSuperAdmin && (
    <OptionGroup title="Super admin" description="Only visible to super admins.">
      <InheritDataSourcesField {...props} />
      {state.reportKind === 'market_survey' && (
        <>
          {advSwitch('generate_ppt', 'Generate PowerPoint')}
          {advSwitch('profilingEnabled', 'Enable profiling')}
          {advSwitch('includeNoveltyScores', 'Include novelty scores')}
          {advSwitch('experimental_features', 'Experimental features')}
        </>
      )}
      {state.mode === 'update' && advSwitch('profilingEnabled', 'Enable profiling')}
    </OptionGroup>
  );

  if (state.mode === 'update') {
    return (
      <>
        <FormSection title="Update strategy" description="How existing products are treated when the report is re-run.">
          <FormControl>
            <FormLabel id="update-strategy" sx={{ position: 'absolute', left: -9999 }}>
              Update strategy
            </FormLabel>
            <RadioGroup
              aria-labelledby="update-strategy"
              value={state.updateStrategy}
              onChange={(_, value) => dispatch(setField('updateStrategy', value as UpdateStrategy))}
            >
              {UPDATE_STRATEGIES.map((s) => (
                <FormControlLabel
                  key={s.value}
                  value={s.value}
                  control={<Radio />}
                  sx={{ alignItems: 'flex-start', mb: 1, '& .MuiRadio-root': { pt: 0.5 } }}
                  label={
                    <Box>
                      <Typography>{s.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {s.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </FormSection>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <OptionGroup title="Other options">
            {advSwitch('plotNorm', 'Plot norms')}
            {advSwitch('wipe_out_imputed_ggvars', 'Wipe out imputed GGVars')}
          </OptionGroup>
          {superAdmin}
        </Box>
      </>
    );
  }

  if (state.reportKind === 'optimization' || state.reportKind === 'mix_and_match') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        {state.reportKind === 'optimization' && (
          <OptionGroup title="Output">
            <SwitchField label="Show norms" checked={opt.showNorms} onChange={(showNorms) => patchOpt({ showNorms })} />
            <SwitchField
              label="Tuning plots"
              checked={opt.tuningPlots}
              onChange={(tuningPlots) => patchOpt({ tuningPlots })}
            />
            <SwitchField label="Plot norms" checked={opt.plotNorm} onChange={(plotNorm) => patchOpt({ plotNorm })} />
            <TextField
              label="Pixel list"
              value={opt.pixelList}
              onChange={(e) => patchOpt({ pixelList: e.target.value })}
              helperText="Optional. One pixel per line, e.g. Pixel: 4.49 PQ, (r89,g12,b4)"
              multiline
              minRows={2}
              sx={{ maxWidth: 560, mt: 1 }}
            />
          </OptionGroup>
        )}
        {superAdmin}
      </Box>
    );
  }

  const textureOff = adv.include_texture ? undefined : 'Requires texture analysis';
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <OptionGroup title="Texture" description="Texture analysis and its outputs.">
        {advSwitch('include_texture', 'Include texture analysis')}
        {advSwitch(
          'texture_data_only',
          'Only texture data available',
          'The products only have texture reviews. Produces a texture-only report.',
          textureOff,
        )}
        {advSwitch(
          'texture_only',
          'Texture only report',
          'Skip flavor analysis even though flavor data exists.',
          textureOff ?? (adv.texture_data_only ? 'Implied by “Only texture data available”' : undefined),
        )}
        {advSwitch('showConfidence', 'Show texture confidence thresholds', undefined, textureOff)}
        {advSwitch('show_TC_se', 'Show texture cluster uncertainty', undefined, textureOff)}
      </OptionGroup>
      <OptionGroup title="Analysis">
        {advSwitch(
          'bootstrap_pq',
          'Bootstrap PQ simulation',
          'Runs 100 bootstrap iterations to estimate 90% confidence intervals on PQ. Runs asynchronously; results appear in the project directory.',
        )}
        {advSwitch('use_hyperlocal_glp', 'Use hyperlocal GLP')}
        {advSwitch('combine_artificial_sweeteners', 'Combine artificial sweeteners')}
        {advSwitch('wipe_out_imputed_ggvars', 'Wipe out imputed GGVars')}
      </OptionGroup>
      <OptionGroup title="Monde Nissin" description="Only for Monde Nissin projects.">
        {advSwitch('only_include_parents', 'Only parents in the target demographic')}
        {advSwitch('include_archetypes', 'Include archetypes')}
      </OptionGroup>
      {superAdmin}
    </Box>
  );
};

export default OptionsStep;
